import { InboxEntry } from '@this+that/models';
import { KeyboardKey } from '@this+that/utils';

import { isEditableTarget } from './shouldIgnoreShortcut';

export interface BoardSelection {
  columnIndex: number;
  rowIndex: number;
}

export const EMPTY_SELECTION: BoardSelection = {
  columnIndex: -1,
  rowIndex: -1,
};

export function clampRowIndex(rowIndex: number, columnLength: number): number {
  if (columnLength === 0) {
    return -1;
  }
  if (rowIndex < 0) {
    return 0;
  }
  return Math.min(rowIndex, columnLength - 1);
}

export function clampSelection(
  selection: BoardSelection,
  columns: InboxEntry[][],
): BoardSelection {
  if (columns.length === 0) {
    return EMPTY_SELECTION;
  }
  if (selection.columnIndex === -1) {
    return selection;
  }
  const columnIndex = Math.min(selection.columnIndex, columns.length - 1);
  const rowIndex = clampRowIndex(
    selection.rowIndex,
    columns[columnIndex]?.length ?? 0,
  );
  return { columnIndex, rowIndex };
}

export function findFirstNonEmptyColumn(columns: InboxEntry[][]): number {
  return columns.findIndex((column) => column.length > 0);
}

export function findNextNonEmptyColumn(
  columns: InboxEntry[][],
  fromIndex: number,
  direction: 1 | -1,
): number {
  for (
    let i = fromIndex + direction;
    i >= 0 && i < columns.length;
    i += direction
  ) {
    if (columns[i].length > 0) {
      return i;
    }
  }
  return -1;
}

export function computeSelectedFlatIndex(
  columns: InboxEntry[][],
  selection: BoardSelection,
): number {
  if (selection.columnIndex < 0 || selection.rowIndex < 0) {
    return -1;
  }
  let offset = 0;
  for (let i = 0; i < selection.columnIndex; i++) {
    offset += columns[i]?.length ?? 0;
  }
  return offset + selection.rowIndex;
}

export function isHorizontalArrowKey(key: string): boolean {
  return key === KeyboardKey.ARROW_LEFT || key === KeyboardKey.ARROW_RIGHT;
}

export function handleArrowKeyDown(event: KeyboardEvent): void {
  if (!isHorizontalArrowKey(event.key)) {
    return;
  }
  if (isEditableTarget(event.target as HTMLElement)) {
    return;
  }
  event.preventDefault();
}
